import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import api from '../api';
import { setUser } from '../store/slices/authSlice';

const steps = [
  { id: 0, title: 'Welcome', icon: '👋' },
  { id: 1, title: 'Profile', icon: '👤' },
  { id: 2, title: 'Preferences', icon: '⚙️' },
  { id: 3, title: 'Complete', icon: '🎉' }
];

function Onboarding() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: '',
    bio: ''
  });
  const [preferences, setPreferences] = useState({
    notifications: true,
    newsletter: false,
    theme: 'light'
  });

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await api.get('/auth/onboarding/status');
      if (response.data.success) {
        const { onboardingStep, profileSetup } = response.data.data;
        setCurrentStep(onboardingStep || 0);
        if (profileSetup) {
          setProfileData(prev => ({ ...prev, ...profileSetup }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
    }
  };

  const handleNext = async () => {
    setLoading(true);
    try {
      let data = {};
      
      if (currentStep === 1) {
        data = { name: profileData.name, phone: profileData.phone, bio: profileData.bio };
      } else if (currentStep === 2) {
        data = { completed: true };
      }

      await api.post('/auth/onboarding/update-step', 
        { step: currentStep + 1, data }
      );

      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Onboarding update failed:', error);
    }
    setLoading(false);
  };

  const handleSkip = () => {
    if (currentStep === 3) {
      navigate('/dashboard');
    } else {
      handleNext();
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="step-content">
            <h2>Welcome to MultiPaaS!</h2>
            <p>We're excited to have you on board. Let's get you set up in just a few steps.</p>
            <div className="welcome-features">
              <div className="feature">
                <span>🚀</span>
                <p>Powerful multi-tenant platform</p>
              </div>
              <div className="feature">
                <span>🔒</span>
                <p>Secure authentication</p>
              </div>
              <div className="feature">
                <span>📊</span>
                <p>Real-time analytics</p>
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="step-content">
            <h2>Set Up Your Profile</h2>
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                placeholder="Enter your name"
              />
            </div>
            <div className="form-group">
              <label>Phone Number (Optional)</label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                placeholder="+1 234 567 8900"
              />
            </div>
            <div className="form-group">
              <label>Bio (Optional)</label>
              <textarea
                value={profileData.bio}
                onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                placeholder="Tell us about yourself..."
                rows={3}
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="step-content">
            <h2>Preferences</h2>
            <div className="preference-item">
              <div>
                <h4>Email Notifications</h4>
                <p>Receive updates about your account</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={preferences.notifications}
                  onChange={(e) => setPreferences({...preferences, notifications: e.target.checked})}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="preference-item">
              <div>
                <h4>Newsletter</h4>
                <p>Get the latest news and updates</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={preferences.newsletter}
                  onChange={(e) => setPreferences({...preferences, newsletter: e.target.checked})}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="preference-item">
              <div>
                <h4>Theme</h4>
                <p>Choose your preferred look</p>
              </div>
              <select
                value={preferences.theme}
                onChange={(e) => setPreferences({...preferences, theme: e.target.value})}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="step-content">
            <div className="complete-icon">🎉</div>
            <h2>You're All Set!</h2>
            <p>Your account is ready. Start exploring and building amazing things.</p>
            <button className="start-btn" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        <div className="steps-indicator">
          {steps.map((step, idx) => (
            <div
              key={step.id}
              className={`step-dot ${idx <= currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}
            >
              <span className="step-icon">{idx < currentStep ? '✓' : step.icon}</span>
            </div>
          ))}
        </div>

        <div className="step-progress">
          Step {currentStep + 1} of {steps.length}
        </div>

        {renderStep()}

        {currentStep < 3 && (
          <div className="actions">
            <button className="skip-btn" onClick={handleSkip}>
              Skip
            </button>
            <button className="next-btn" onClick={handleNext} disabled={loading}>
              {loading ? 'Saving...' : currentStep === 2 ? 'Complete' : 'Next'}
            </button>
          </div>
        )}
      </div>

      <style>{`
        .onboarding-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
        }
        .onboarding-container {
          background: white;
          padding: 40px;
          border-radius: 15px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          max-width: 500px;
          width: 100%;
        }
        .steps-indicator {
          display: flex;
          justify-content: center;
          gap: 15px;
          margin-bottom: 30px;
        }
        .step-dot {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #eee;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }
        .step-dot.active {
          background: #667eea;
          color: white;
        }
        .step-dot.completed {
          background: #4CAF50;
          color: white;
        }
        .step-icon { font-size: 18px; }
        .step-progress {
          text-align: center;
          color: #888;
          margin-bottom: 20px;
          font-size: 14px;
        }
        .step-content h2 {
          text-align: center;
          margin-bottom: 15px;
          color: #333;
        }
        .step-content p {
          text-align: center;
          color: #666;
          margin-bottom: 25px;
        }
        .welcome-features {
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: 20px;
        }
        .feature {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        .feature span { font-size: 24px; }
        .feature p { margin: 0; text-align: left; }
        .form-group { margin-bottom: 20px; }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #555;
          font-weight: 500;
        }
        .form-group input, .form-group textarea, .form-group select {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          box-sizing: border-box;
        }
        .form-group input:focus, .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
        }
        .preference-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          border-bottom: 1px solid #eee;
        }
        .preference-item h4 { margin: 0 0 5px; color: #333; }
        .preference-item p { margin: 0; font-size: 13px; color: #888; }
        .toggle { position: relative; display: inline-block; width: 50px; height: 28px; }
        .toggle input { opacity: 0; width: 0; height: 0; }
        .slider {
          position: absolute;
          cursor: pointer;
          top: 0; left: 0; right: 0; bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 28px;
        }
        .slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 4px;
          bottom: 4px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .slider { background: #667eea; }
        .toggle input:checked + .slider:before { transform: translateX(22px); }
        .complete-icon {
          font-size: 60px;
          text-align: center;
          margin-bottom: 20px;
        }
        .start-btn, .actions .next-btn {
          width: 100%;
          padding: 14px;
          background: #667eea;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          cursor: pointer;
          transition: background 0.3s;
        }
        .start-btn:hover, .next-btn:hover:not(:disabled) { background: #5568d3; }
        .actions {
          display: flex;
          gap: 15px;
          margin-top: 30px;
        }
        .skip-btn {
          flex: 1;
          padding: 14px;
          background: transparent;
          border: 1px solid #ddd;
          border-radius: 8px;
          color: #666;
          cursor: pointer;
        }
        .next-btn { flex: 2; }
      `}</style>
    </div>
  );
}

export default Onboarding;